import { QueryFactory } from '@lmt/shared-types';
import { useEnableMutation } from '@lmt/shared-types/src/api/api-client/PrivilegedModeTotpQuery';
import { TotpSixDigitsInput } from '@lmt/unicorn-shared/app/components/privileged-mode/TotpSixDigitsInput';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTenantAdministratorRoleId } from '@lmt/unicorn-shared/app/features/roles/selectors';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useEventCallback } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type TotpState = { code: string; errorText?: string };
const privilegedModeTotpModal = 'privileged-mode-totp-modal';

const isPrivilegedHotkey = (e: KeyboardEvent) => {
  if (e.code !== 'KeyT') return false;
  const winCombo = e.ctrlKey && e.shiftKey && e.altKey;
  const macCombo = e.metaKey && e.shiftKey && e.altKey;
  return winCombo || macCombo;
};

const usePrivilegedModeHotkey = () => {
  const i18n = useTranslation();
  const i18next = useScopedTranslation('privileged_mode');
  const modals = useModal();
  const enableMutation = useEnableMutation();
  const claims = useClaims();
  const queryCache = useQueryClient();
  const userQuery = QueryFactory.UserQuery.useGetUserQuery(claims.id, {
    enabled: !!claims.id,
  });
  const adminRoleId = useTenantAdministratorRoleId();

  async function invalidatePermissions() {
    await queryCache.invalidateQueries({
      queryKey: QueryFactory.UserQuery.getCurrentUserInfoQueryKey(),
    });
  }

  const trySubmit = useEventCallback(
    async (
      value: TotpState | null | undefined,
      setValue?: (v: TotpState) => void,
    ) => {
      if (!value) return false;

      const setError = (text: string) => {
        if (setValue) setValue({ ...value, errorText: text });
        else value.errorText = text;
      };

      const code = (value.code ?? '').replace(/\D/g, '').slice(0, 6);
      if (code.length !== 6) {
        setError(i18n.t('errors.min_length', { minLength: 6 }));
        return false;
      }

      try {
        await enableMutation.mutateAsync({ code });
        toast(i18next.t('activate_successful_toast'), { type: 'success' });
        await invalidatePermissions();
        return true;
      } catch (err: any) {
        setError(convertToErrorString(err, { excludeFieldName: true }));
        return false;
      }
    },
  );

  const handleHotkey = useEventCallback(async (e: KeyboardEvent) => {
    if (!isPrivilegedHotkey(e)) return;
    e.preventDefault();

    const target = e.target as HTMLElement | null;
    const tagName = target?.tagName?.toLowerCase();
    if (
      tagName === 'input' ||
      tagName === 'textarea' ||
      target?.isContentEditable
    )
      return; // if we are in the text editor or document and hotkey was pressed for another purposes

    const isTenantAdmin =
      !!adminRoleId &&
      !!userQuery.data &&
      userQuery.data.roles.includes(adminRoleId);

    if (!isTenantAdmin) return;

    await modals.showCustom<TotpState>({
      title: i18next.t('activation_modal_title'),
      defaultValue: { code: '', errorText: undefined },
      okButtonText: i18next.t('enable_button'),
      Component: (props) => (
        <div data-test-id={privilegedModeTotpModal}>
          <TotpSixDigitsInput
            value={props.value?.code ?? ''}
            errorText={props.value?.errorText}
            onChange={(next) =>
              props.setValue({
                ...(props.value ?? { code: '' }),
                code: next,
                errorText: undefined,
              })
            }
            onEnter={async () => {
              if (await trySubmit(props.value, props.setValue))
                props.onClose(props.value);
            }}
          />
        </div>
      ),
      validate: trySubmit,
    });
  });

  useEffect(() => {
    document.addEventListener('keyup', handleHotkey);
    return () => document.removeEventListener('keyup', handleHotkey);
  }, [handleHotkey]);
};

export const PrivilegedModeHotkeys: React.FC = () => {
  usePrivilegedModeHotkey();
  return null;
};
